import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  inject,
} from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { MatDialog } from '@angular/material/dialog';
import { AppConstants } from './../../../app.constants';
import {
  AuthorizationService,
  RBAC,
} from '../../../core/services/authorization.service';
import { _roleEnumUserClass } from '../../../utils/role.utils';
import { resolveLocalizedLink } from '../../../utils/localized-link.utils';
import { gitVersion } from 'assets/json/json.interfaces';
import { environment } from 'environments/environment';
import { CurrentViewportBreakpoint } from '../layouts/basicLayout.component';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { ServiceBase } from 'app/core/services/service.base';
import { AuthStateService } from 'app/core/services/auth-state.service';
import { DialogFreeTierComponent } from '../dialog-free-tier/dialog-free-tier.component';
import { canUseFieldNotebook } from 'app/utils/field-notebook-access.utils';
import { canProvideRemoteSupport } from 'app/utils/remote-support-access.utils';

const SUPPORT_ROUTE = '/suporte-tecnico';
const REMOTE_SUPPORT_ROUTE = '/suporte-tecnico/acesso-remoto';

@Component({
  standalone: false,
  selector: 'app-sidebar',
  templateUrl: './sidebar-menu.component.html',
  styleUrls: ['./sidebar-menu.component.scss'],
})
export class SidebarMenuComponent implements OnInit, OnDestroy {
  @Input() isSidebarOpen = false;
  @Input() resizeEventEmitter: EventEmitter<CurrentViewportBreakpoint>;
  @Input() toggleSidebarSubject: Subject<boolean | void>;

  private readonly authState = inject(AuthStateService);
  private readonly destroy$ = new Subject<void>();

  userRole = '';
  /** Members of a remote support group see the remote support entry. */
  remoteSupportVisible = false;
  readonly remoteSupportRoute = REMOTE_SUPPORT_ROUTE;
  public auth: RBAC;
  public environmentName: typeof environment.environmentName;
  public versaoGit: string;

  ROLE_USERS_ENUM = _roleEnumUserClass.ROLE_ENUM;

  constructor(
    public router: Router,
    public translate: TranslateService,
    public authorizationService: AuthorizationService,
    private readonly toastr: ToastrService,
    private readonly dialog: MatDialog,
  ) {
    this.auth = authorizationService.gethandler();
    this.versaoGit = gitVersion.version;
    this.environmentName = environment.environmentName;
  }

  close(): void {
    this.isSidebarOpen = false;
  }

  ngOnInit() {
    this.authState.user$
      .pipe(
        filter((user) => !!user),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.auth = this.authorizationService.gethandler();
      });

    // The claim lives on the token, which is replaced after the user on every sign-in and refresh.
    this.authState.token$.pipe(takeUntil(this.destroy$)).subscribe((token) => {
      this.remoteSupportVisible = canProvideRemoteSupport(token);
    });

    if (!!this.toggleSidebarSubject) {
      this.toggleSidebarSubject.subscribe((isSidebarOpen) => {
        if (typeof isSidebarOpen == 'undefined')
          this.isSidebarOpen = !this.isSidebarOpen;
        else this.isSidebarOpen = isSidebarOpen;
      });
    }
    if (!!this.resizeEventEmitter) {
      this.resizeEventEmitter.subscribe(
        (currentViewportBreakpoint: CurrentViewportBreakpoint) => {
          if (
            currentViewportBreakpoint.tablet ||
            currentViewportBreakpoint.mobile
          ) {
            this.isSidebarOpen = false;
          }
          if (currentViewportBreakpoint.desktop) {
            this.isSidebarOpen = true;
          }
        },
      );
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** The field notebook is an internal pilot: other accounts do not see the entry. */
  get notebookVisible(): boolean {
    return canUseFieldNotebook(this.authState.getUser()?.id);
  }

  get notebookRoute(): string | null {
    if (this.isRoleFreetier()) {
      return null;
    }
    return '/field-notebook';
  }

  listFields(): void {
    const currentURL = window.location.href;
    switch (true) {
      case currentURL.includes('my-fields/gerenciamento-de-talhoes'):
        this.router.navigate(['/my-fields']);
        break;
      case currentURL.includes('my-fields'):
        window.location.reload();
        break;
      default:
        this.router.navigate(['/my-fields']);
    }
  }

  /**
   * Address of the Tecomec Academy portal, opened in a new tab.
   *
   * @remarks
   * External training portal, public to every logged user, so no role check is
   * applied and the item is never locked behind the premium badge.
   */
  get academyLink(): string {
    return AppConstants.EXTERNAL_LINKS.AGRES_ACADEMY;
  }

  /** "About us" page of the company site in the app language. */
  get aboutLink(): string {
    return resolveLocalizedLink(
      AppConstants.EXTERNAL_LINKS.COMPANY_ABOUT,
      this.translate.currentLang,
    );
  }
  isAdmin(): boolean {
    const role = this.authorizationService.getLoggedUserRole();
    return role === this.ROLE_USERS_ENUM.Admin;
  }

  isRoleFreetier(): boolean {
    return _roleEnumUserClass.isRoleFreetier(this.authState.getUser());
  }

  /** Support, except remote support, which has an entry of its own. */
  isSupportActive(): boolean {
    return (
      this.activeRoute(SUPPORT_ROUTE) && !this.activeRoute(REMOTE_SUPPORT_ROUTE)
    );
  }

  activeRoute(routename: string): boolean {
    if (routename === '/') {
      return this.router.url === '/' || this.router.url === '';
    }
    const isActive = this.router.isActive(routename, {
      matrixParams: 'exact',
      queryParams: 'exact',
      paths: 'subset',
      fragment: 'exact',
    });
    return isActive;
  }

  handlePremiumLinkClick(event: Event, route: string): void {
    if (!this.isRoleFreetier()) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    this.translate.get('dialog-free-tier.title').subscribe((message) => {
      this.toastr.warning(message, '', {
        timeOut: 4000,
        progressBar: true,
        positionClass: 'toast-top-right',
      });
    });

    // Abre o modal de atualização de perfil
    const dialogRef = this.dialog.open(DialogFreeTierComponent, {
      width: '600px',
      height: '240px',
      disableClose: false,
      enterAnimationDuration: '200ms',
      exitAnimationDuration: '200ms',
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result === 'form') {
        this.router.navigate(['/manage/my-profile']);
        this.close();
      }
    });
  }
}
